import { useEffect } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import AboutSideNav from '../../../../components/about/AboutSideNav';

// ===== Access & Location (EN mirror) — full rebuild, Implementer A scope =====
// Design language mirrors the JA access page (navy #1a2942 / brass #a38e65 / BTN-08 / gold accent
//   headings / large-font key data for readability). Section types all distinct; two new variants
//   (location key card, horizontal numbered steps). Fixed facts unchanged. No invented figures.
//   EN pages contain no Japanese characters (SEO rule 1-3).

export default function EnAccessPage() {
  useEffect(() => {
    document.title = 'Access & Location | Japan Butler Association';

    const setMeta = (name: string, content: string) => {
      let m = document.querySelector('meta[name="' + name + '"]');
      if (!m) { m = document.createElement('meta'); m.setAttribute('name', name); document.head.appendChild(m); }
      m.setAttribute('content', content);
    };
    setMeta('description', 'Location and access for the Japan Butler Association headquarters: 2-7-4 Aomi, Koto-ku, Tokyo, in the Odaiba area. About a 5-minute walk from Tokyo International Cruise Terminal Station. Visits by appointment only. Tel +81-3-6824-4086.');

    const setLink = (rel: string, href: string, hreflang?: string) => {
      const sel = hreflang ? 'link[rel="' + rel + '"][hreflang="' + hreflang + '"]' : 'link[rel="' + rel + '"]';
      let l = document.querySelector(sel);
      if (!l) { l = document.createElement('link'); l.setAttribute('rel', rel); if (hreflang) l.setAttribute('hreflang', hreflang); document.head.appendChild(l); }
      l.setAttribute('href', href);
    };
    setLink('canonical', 'https://butler-association.jp/en/about/access');
    setLink('alternate', 'https://butler-association.jp/about/access', 'ja');
    setLink('alternate', 'https://butler-association.jp/en/about/access', 'en');
    setLink('alternate', 'https://butler-association.jp/about/access', 'x-default');

    document.documentElement.lang = 'en';
  }, []);

  const breadcrumbs = [
    { label: 'Home', href: '/en/' },
    { label: 'About Us', href: '/en/about' },
    { label: 'Access & Location' }
  ];

  const aboutNav = [
    { label: 'Overview', href: '/en/about/overview' },
    { label: 'Mission', href: '/en/about/mission' },
    { label: 'History', href: '/en/about/history' },
    { label: 'Organization', href: '/en/about/organization' },
    { label: 'Activities', href: '/en/about/activities' },
    { label: 'Access', href: '/en/about/access' }
  ];

  const BTN08_ON_NAVY = "relative inline-flex items-center justify-center bg-[#a38e65] text-white font-semibold border border-[#1a2942] px-[42px] py-[16px] text-sm tracking-[0.2em] font-['Noto_Serif_JP',serif] transition-colors duration-300 hover:bg-[#1a2942] hover:text-[#f8f6f0] hover:border-[#a38e65] cursor-pointer whitespace-nowrap before:content-[''] before:absolute before:inset-1 before:border before:border-[#1a2942]/40 before:pointer-events-none before:transition-colors before:duration-300 hover:before:border-[#a38e65]/50";

  const detailRows = [
    { label: 'Name', value: 'Japan Butler Association' },
    { label: 'Corp. No.', value: '2010005039584' },
    { label: 'E-mail', value: 'info@butler-association.jp' },
    { label: 'Hours', value: 'Open year-round (visits by appointment only)' },
  ];

  const trainRoutes = [
    { line: 'Yurikamome', station: 'Tokyo International Cruise Terminal', walk: 'approx. 5 min walk', notes: ['15 min from Shimbashi Station', '10 min from Toyosu Station', 'Exit and head toward Aomi 2-chome'] },
    { line: 'Yurikamome', station: 'Telecom Center Station', walk: 'approx. 5 min walk', notes: ['16 min from Shimbashi Station', '9 min from Toyosu Station', 'Exit and head toward Aomi'] },
    { line: 'Rinkai Line', station: 'Tokyo Teleport Station', walk: 'approx. 15 min walk', notes: ['13 min from Osaki Station', '8 min from Shin-Kiba Station', 'Take Exit B toward Aomi'] },
  ];

  const travelTimes = [
    { from: 'Shimbashi', detail: 'approx. 15 min by Yurikamome', fare: 'Fare 330 yen' },
    { from: 'Toyosu', detail: 'approx. 10 min by Yurikamome', fare: 'Fare 270 yen' },
    { from: 'Shinagawa', detail: 'approx. 16 min by Rinkai Line (via Osaki)', fare: 'Fare 370 yen' },
    { from: 'Tokyo', detail: 'approx. 25 min (JR then Yurikamome)', fare: '' },
    { from: 'Shibuya', detail: 'approx. 30 min (Saikyo then Rinkai Line)', fare: '' },
  ];

  const carRoutes = [
    { route: 'Shuto Expwy Bayshore Route', detail: 'About 5 min from the Rinkai Fukutoshin exit, or 8 min from the Ariake exit. The Bayshore Route offers the most accessible approach.' },
    { route: 'Shuto Expwy Route 11 Daiba Line', detail: 'About 7 min from the Daiba exit, or 10 min from the Ariake exit. Convenient when coming from central Tokyo.' },
    { route: 'By general roads', detail: 'Accessible via the Rainbow Bridge, via National Route 357 (the Bayshore Road), or via the Aomi Vertical Line.' },
  ];

  const parkings = [
    { name: 'Building Parking', tag: 'Paid, mechanical type', lines: ['Please let us know when you make your reservation. Spaces are limited.', 'As it is a mechanical system, vehicle size is restricted. Please call for details.', 'If full, please use a nearby parking facility.'] },
    { name: 'Times Aomi 2-chome', tag: 'Nearby, coin parking', lines: ['2-2 Aomi, Koto-ku, Tokyo 135-0064', 'About a 3-minute walk from our headquarters', 'A 24-hour coin parking facility.', 'For rates and details, please contact the parking operator.'] },
  ];

  const visitSteps = [
    { no: '01', title: 'By Appointment Only', text: 'All visits — for seminar attendance, member services, or consultations — are by prior appointment. This ensures a high standard of service, so please refrain from visiting without a reservation.' },
    { no: '02', title: 'How to Reserve', text: 'By phone (+81-3-6824-4086) or email (info@butler-association.jp), please share your name and contact details, the purpose of your visit, preferred dates (1st to 3rd choice), the number of visitors, and whether you will arrive by car.' },
    { no: '03', title: 'On the Day', text: 'Please arrive about 5 minutes before your appointment. Give your name and purpose at reception; first-time visitors receive a tour of the premises. If you will be late or need to cancel, please call us early.' },
  ];

  return (
    <Layout>
      {/* Hero — full-width billboard */}
      <section className="relative bg-[#1a2942] overflow-hidden min-h-[520px] lg:min-h-[600px] flex items-center">
        <img
          src="/images/gen2/jba-access-aomi-office-exterior-en-01.jpg"
          alt="Illustrative modern office exterior concept for visitor guidance in the Aomi, Odaiba headquarters area"
          className="absolute inset-0 w-full h-full object-cover object-[50%_15%]"
        />
        <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(16,20,26,0.92)_0%,rgba(16,20,26,0.72)_48%,rgba(16,20,26,0.35)_100%)]"></div>
        <div className="relative z-10 w-full max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 pt-32 pb-16 lg:pt-36 lg:pb-20">
          <div className="max-w-[680px]">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.32em] text-xs sm:text-[13px] text-[#d7b779] mb-4">Access &amp; Location</span>
            <div className="w-12 h-px bg-[#a38e65] mb-6"></div>
            <h1 className="jba-display font-normal text-white text-[clamp(28px,3.4vw,44px)] leading-[1.4] tracking-[0.04em] mb-6">
              <span className="block">To the Butler Salon</span>
              <span className="block">in Odaiba, by the bay</span>
            </h1>
            <div className="space-y-5">
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                The headquarters of the Japan Butler Association stands in Aomi, Koto-ku — a corner of the Odaiba area. In this international waterfront subcenter overlooking Tokyo Bay, access from across the city is excellent.
              </p>
              <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-white/90 leading-[1.95]">
                As visits are by appointment only, please contact us in advance.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* パンくず: ビルボード直後に配置（Yuichi 2026-07-17 指摘②）。共通Breadcrumbのヘッダー逃げpaddingを子セレクタで通常paddingへ上書き */}
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      {/* Body = side nav + content */}
      <div className="bg-white">
        <div className="max-w-[1680px] mx-auto px-4 sm:px-6 lg:px-10 py-12 sm:py-16 lg:py-20">
          <div className="grid lg:grid-cols-[248px_1fr] gap-8 lg:gap-14">

            <AboutSideNav items={aboutNav} activeHref="/en/about/access" eyebrow="About Us" heading="About Us" />

            <div className="min-w-0 space-y-16 sm:space-y-20">

              {/* I. Location — location key card (new variant 1) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">I. Location</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Headquarters &amp; Basic Information
                </h2>
                <div className="grid lg:grid-cols-[1fr_340px] gap-8 lg:gap-12 items-stretch">
                  <div className="bg-[#1a2942] text-[#f8f6f0] p-8 sm:p-10 lg:p-12 flex flex-col justify-center">
                    <span className="inline-flex self-start items-center gap-2 border border-[#a38e65] text-[#d7b779] font-['Noto_Serif_JP',serif] text-xs tracking-[0.16em] px-4 py-1.5 mb-7">By Appointment Only</span>
                    <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#a38e65] mb-2">Address</span>
                    <p className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-3xl lg:text-[30px] leading-[1.45] tracking-[0.01em] mb-1">
                      2-7-4 Aomi,<br />Koto-ku, Tokyo
                    </p>
                    <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] tracking-[0.06em] mb-8">135-0064, Japan</p>
                    <div className="border-t border-[#a38e65]/40 pt-7">
                      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.2em] text-[11px] text-[#a38e65] mb-2">Telephone</span>
                      <a href="tel:+81-3-6824-4086" className="jba-display font-medium text-[#f8f6f0] text-2xl sm:text-[30px] tracking-[0.04em] hover:text-[#d7b779] transition-colors duration-300">+81-3-6824-4086</a>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#ded8cc] leading-[1.85] mt-2">To visit or consult, please reserve first by phone or email.</p>
                    </div>
                  </div>
                  <img
                    src="/images/gen2/jba-access-aomi-office-exterior-en-02.jpg"
                    alt="Illustrative visitor approach to a modern office exterior evoking the Aomi, Odaiba headquarters area"
                    className="w-full h-64 lg:h-full min-h-[260px] object-cover object-[50%_15%] border border-[#a38e65]/30"
                  />
                </div>
                <dl className="mt-8 border-t-2 border-[#1a2942] grid sm:grid-cols-2 gap-x-12">
                  {detailRows.map((row) => (
                    <div key={row.label} className="flex flex-col sm:flex-row gap-1 sm:gap-6 border-b border-[#e2ddd2] py-4">
                      <dt className="sm:w-[112px] sm:shrink-0 font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.06em] pt-0.5">{row.label}</dt>
                      <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#1a2942] leading-[1.85]">{row.value}</dd>
                    </div>
                  ))}
                </dl>
                <div className="space-y-5 mt-6">
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] max-w-3xl">
                    Aomi, Koto-ku, where our headquarters stands, is a corner of Odaiba — an advanced waterfront subcenter facing Tokyo Bay.
                  </p>
                  <p className="font-['Noto_Serif_JP',serif] text-[15px] text-[#4a4638] leading-[1.95] max-w-3xl">
                    Access is excellent not only from within Tokyo’s 23 wards but also from Haneda Airport, about 30 minutes away, making it convenient for visitors from home and abroad.
                  </p>
                </div>
              </section>

              {/* II. By Train — three-column card grid (catalog N05, large-font station/walk) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">II. By Train</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Coming by train
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  The Odaiba area is served by two lines — the Yurikamome and the Rinkai Line — with convenient access from across the city. The Yurikamome, at Tokyo International Cruise Terminal or Telecom Center Station, is the most convenient way to reach us.
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7 mb-10">
                  {trainRoutes.map((r, i) => (
                    <div key={i} className="border border-[#e2ddd2] bg-white p-7 flex flex-col">
                      <span className="block font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] tracking-[0.06em] mb-1.5">{r.line}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl leading-[1.45] mb-4">{r.station}</h3>
                      <p className="jba-display font-medium text-[#a38e65] text-xl sm:text-2xl lg:text-[25px] tracking-[0.01em] mb-5">{r.walk}</p>
                      <ul className="border-t border-[#e2ddd2] pt-4 space-y-1.5 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.8]">
                        {r.notes.map((n) => (<li key={n}>{n}</li>))}
                      </ul>
                    </div>
                  ))}
                </div>
                <div className="bg-[#f8f6f0] border border-[#e2ddd2] p-7 sm:p-9">
                  <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-6">Estimated travel times from major stations</h3>
                  <dl className="border-t-2 border-[#1a2942]">
                    {travelTimes.map((t) => (
                      <div key={t.from} className="flex flex-wrap items-baseline gap-x-5 gap-y-1 border-b border-[#e2ddd2] py-4">
                        <dt className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl w-32 shrink-0">{t.from}</dt>
                        <dd className="flex-1 min-w-0 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638]">{t.detail}</dd>
                        {t.fare && <dd className="font-['Noto_Serif_JP',serif] text-sm text-[#8f7a56] tracking-[0.04em]">{t.fare}</dd>}
                      </div>
                    ))}
                  </dl>
                </div>
              </section>

              {/* III. By Car — definition list + stacked parking cards */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">III. By Car</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Coming by car
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  The Odaiba area offers good access from the Shuto Expressway. When using a navigation system, please enter the address "2-7-4 Aomi, Koto-ku, Tokyo" or the phone number "+81-3-6824-4086." As spaces are limited, please also consider public transport.
                </p>
                <dl className="border-t-2 border-[#1a2942] mb-10">
                  {carRoutes.map((c) => (
                    <div key={c.route} className="flex flex-col sm:flex-row gap-1.5 sm:gap-8 border-b border-[#e2ddd2] py-5">
                      <dt className="sm:w-[260px] sm:shrink-0 jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{c.route}</dt>
                      <dd className="flex-1 font-['Noto_Serif_JP',serif] text-[15px] sm:text-base text-[#4a4638] leading-[1.9]">{c.detail}</dd>
                    </div>
                  ))}
                </dl>
                <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-5">Parking information</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  {parkings.map((p) => (
                    <div key={p.name} className="border border-[#e2ddd2] bg-white p-7">
                      <div className="flex items-baseline justify-between gap-3 mb-4 border-b border-[#e2ddd2] pb-4">
                        <h4 className="jba-display font-medium text-[#1a2942] text-lg leading-[1.4]">{p.name}</h4>
                        <span className="font-['Playfair_Display',serif] italic text-[#8f7a56] text-[13px] shrink-0">{p.tag}</span>
                      </div>
                      <ul className="space-y-2 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">
                        {p.lines.map((l) => (<li key={l} className="flex gap-2.5"><span className="text-[#a38e65] leading-[1.7]">&mdash;</span><span>{l}</span></li>))}
                      </ul>
                    </div>
                  ))}
                </div>
              </section>

              {/* IV. Map */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">IV. Map</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Location map
                </h2>
                <div className="border border-[#a38e65]/30">
                  <iframe
                    src="https://maps.google.com/maps?q=the%20SOHO%202-7-4%20Aomi%20Koto-ku%20Tokyo&z=17&output=embed"
                    width="100%"
                    height="440"
                    style={{ border: 0, display: 'block' }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Japan Butler Association headquarters location map"
                  ></iframe>
                  <p className="bg-[#f8f6f0] border-t border-[#e2ddd2] px-6 py-4 font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.85]">
                    The map is for reference. Please confirm detailed directions in advance. If you lose your way on a first visit, feel free to call us (Tel +81-3-6824-4086); our staff will guide you.
                  </p>
                </div>
              </section>

              {/* V. Before You Visit — horizontal numbered steps (new variant 2) */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">V. Before You Visit</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-3">
                  Before your visit
                </h2>
                <p className="font-['Noto_Serif_JP',serif] text-base text-[#333] leading-[1.95] max-w-3xl mb-9">
                  For a smooth visit, we have summarized the flow in three steps. All visits to the association are by appointment only. Please review the following and contact us in advance.
                </p>
                <div className="grid md:grid-cols-3 gap-6 lg:gap-7">
                  {visitSteps.map((s) => (
                    <div key={s.no} className="border-t-2 border-[#a38e65] pt-6">
                      <span className="block font-['Playfair_Display',serif] text-[#d7b779] text-3xl sm:text-4xl leading-none mb-3">{s.no}</span>
                      <h3 className="jba-display font-medium text-[#1a2942] text-lg sm:text-xl mb-3 leading-[1.45]">{s.title}</h3>
                      <p className="font-['Noto_Serif_JP',serif] text-sm text-[#4a4638] leading-[1.9]">{s.text}</p>
                    </div>
                  ))}
                </div>
              </section>

              {/* VI. Reservations — navy CTA panel */}
              <section>
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">VI. Reservations</span>
                <h2 className="jba-display font-medium text-[#1a2942] text-2xl sm:text-3xl lg:text-[31px] leading-[1.5] tracking-[0.02em] mb-6">
                  Reservations &amp; Inquiries
                </h2>
                <div className="bg-[#1a2942] border border-[#a38e65]/45 p-8 sm:p-10 lg:p-12">
                  <p className="font-['Noto_Serif_JP',serif] text-base sm:text-lg text-[#e7e2d8] leading-[1.95] max-w-3xl mb-8">
                    Visits to the association are by appointment only. If you have any questions about access, parking, or the surrounding area, please feel free to contact us. Our staff will be glad to help.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                    <a href="tel:+81-3-6824-4086" className={BTN08_ON_NAVY}>Call to Reserve +81-3-6824-4086</a>
                    <a href="/en/contact/" className={BTN08_ON_NAVY}>Contact Us</a>
                  </div>
                </div>
              </section>

            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}
